/**
 * Tenant Branding GraphQL Queries
 *
 * Provides queries and mutations for TenantBrandingRepository integration.
 * Backend: graphql_gateway/src/metadata_api/tenant_branding_graphql.rs
 *
 * Features:
 * - Tenant branding CRUD operations
 * - Industry-specific defaults (healthcare, dental, legal, finance)
 * - Terminology mapping (FERP terms → Industry terms)
 * - Custom domain and subdomain support
 * - Visual branding (colors, logos, fonts)
 * - Feature toggles (FERP branding visibility, help center, support chat)
 *
 * Life-Critical: Production-ready with complete error handling
 * Revenue Impact: Part of $5M+/year white-label platform
 * Issue: #312 - White-Label System Implementation
 */

import { gql } from '@apollo/client';

/**
 * Get tenant branding by tenant ID
 */
export const GET_TENANT_BRANDING = gql`
  query GetTenantBranding($tenantId: String!) {
    getTenantBranding(tenantId: $tenantId) {
      tenantId
      logoUrl
      faviconUrl
      primaryColor
      secondaryColor
      accentColor
      fontFamily
      companyName
      companyTagline
      supportEmail
      supportPhone
      websiteUrl
      terminology
      customDomain
      subdomain
      websiteSlug
      address
      hours
      licenseType
      services
      accreditations
      hideFerpBranding
      showHelpCenter
      showSupportChat
      createdAt
      createdBy
      updatedAt
      updatedBy
      version
    }
  }
`;

/**
 * Get tenant branding by subdomain
 * Used for multi-tenant routing (e.g., healthcare-clinic.ferp.com)
 */
export const GET_TENANT_BRANDING_BY_SUBDOMAIN = gql`
  query GetTenantBrandingBySubdomain($subdomain: String!) {
    getTenantBrandingBySubdomain(subdomain: $subdomain) {
      tenantId
      logoUrl
      faviconUrl
      primaryColor
      secondaryColor
      accentColor
      fontFamily
      companyName
      companyTagline
      supportEmail
      supportPhone
      websiteUrl
      terminology
      customDomain
      subdomain
      websiteSlug
      address
      hours
      licenseType
      services
      accreditations
      hideFerpBranding
      showHelpCenter
      showSupportChat
      createdAt
      createdBy
      updatedAt
      updatedBy
      version
    }
  }
`;

/**
 * Get tenant branding by custom domain
 * Used for white-label domains (e.g., healthcare-manager.com)
 */
export const GET_TENANT_BRANDING_BY_CUSTOM_DOMAIN = gql`
  query GetTenantBrandingByCustomDomain($customDomain: String!) {
    getTenantBrandingByCustomDomain(customDomain: $customDomain) {
      tenantId
      logoUrl
      faviconUrl
      primaryColor
      secondaryColor
      accentColor
      fontFamily
      companyName
      companyTagline
      supportEmail
      supportPhone
      websiteUrl
      terminology
      customDomain
      subdomain
      websiteSlug
      address
      hours
      licenseType
      services
      accreditations
      hideFerpBranding
      showHelpCenter
      showSupportChat
      createdAt
      createdBy
      updatedAt
      updatedBy
      version
    }
  }
`;

/**
 * List all tenant brandings
 * Admin only - for managing all tenants
 */
export const LIST_TENANT_BRANDINGS = gql`
  query ListTenantBrandings {
    listTenantBrandings {
      tenantId
      logoUrl
      faviconUrl
      primaryColor
      secondaryColor
      accentColor
      fontFamily
      companyName
      companyTagline
      supportEmail
      supportPhone
      websiteUrl
      terminology
      customDomain
      subdomain
      websiteSlug
      address
      hours
      licenseType
      services
      accreditations
      hideFerpBranding
      showHelpCenter
      showSupportChat
      createdAt
      createdBy
      updatedAt
      updatedBy
      version
    }
  }
`;

/**
 * Translate FERP term to industry-specific term
 * Example: "workflow" → "procedure" (healthcare), "matter" (legal)
 */
export const TRANSLATE_TERM = gql`
  query TranslateTerm($input: TranslateTermInput!) {
    translateTerm(input: $input) {
      originalTerm
      translatedTerm
      tenantId
    }
  }
`;

/**
 * Create new tenant branding
 */
export const CREATE_TENANT_BRANDING = gql`
  mutation CreateTenantBranding($input: CreateTenantBrandingInput!) {
    createTenantBranding(input: $input) {
      tenantId
      logoUrl
      faviconUrl
      primaryColor
      secondaryColor
      accentColor
      fontFamily
      companyName
      companyTagline
      supportEmail
      supportPhone
      websiteUrl
      terminology
      customDomain
      subdomain
      websiteSlug
      address
      hours
      licenseType
      services
      accreditations
      hideFerpBranding
      showHelpCenter
      showSupportChat
      createdAt
      createdBy
      updatedAt
      updatedBy
      version
    }
  }
`;

/**
 * Update existing tenant branding
 */
export const UPDATE_TENANT_BRANDING = gql`
  mutation UpdateTenantBranding($input: UpdateTenantBrandingInput!) {
    updateTenantBranding(input: $input) {
      tenantId
      logoUrl
      faviconUrl
      primaryColor
      secondaryColor
      accentColor
      fontFamily
      companyName
      companyTagline
      supportEmail
      supportPhone
      websiteUrl
      terminology
      customDomain
      subdomain
      websiteSlug
      address
      hours
      licenseType
      services
      accreditations
      hideFerpBranding
      showHelpCenter
      showSupportChat
      createdAt
      createdBy
      updatedAt
      updatedBy
      version
    }
  }
`;

/**
 * Reset tenant branding to defaults
 * Optionally apply industry-specific defaults (healthcare, dental, legal, finance)
 */
export const RESET_TENANT_BRANDING = gql`
  mutation ResetTenantBranding($input: ResetTenantBrandingInput!) {
    resetTenantBranding(input: $input) {
      tenantId
      logoUrl
      faviconUrl
      primaryColor
      secondaryColor
      accentColor
      fontFamily
      companyName
      companyTagline
      supportEmail
      supportPhone
      websiteUrl
      terminology
      customDomain
      subdomain
      websiteSlug
      address
      hours
      licenseType
      services
      accreditations
      hideFerpBranding
      showHelpCenter
      showSupportChat
      createdAt
      createdBy
      updatedAt
      updatedBy
      version
    }
  }
`;

/**
 * Delete tenant branding
 */
export const DELETE_TENANT_BRANDING = gql`
  mutation DeleteTenantBranding($input: DeleteTenantBrandingInput!) {
    deleteTenantBranding(input: $input)
  }
`;

/**
 * TypeScript types for GraphQL responses
 */

export interface TenantBranding {
  tenantId: string;
  logoUrl?: string | null;
  faviconUrl?: string | null;
  primaryColor: string;
  secondaryColor: string;
  accentColor: string;
  fontFamily: string;
  companyName: string;
  companyTagline?: string | null;
  supportEmail?: string | null;
  supportPhone?: string | null;
  websiteUrl?: string | null;
  terminology: Record<string, string>;
  customDomain?: string | null;
  subdomain: string;
  websiteSlug?: string | null;
  address?: string | null;
  hours?: string | null;
  licenseType?: string | null;
  services: string[];
  accreditations: string[];
  hideFerpBranding: boolean;
  showHelpCenter: boolean;
  showSupportChat: boolean;
  createdAt: string;
  createdBy: string;
  updatedAt: string;
  updatedBy: string;
  version: number;
}

export interface TranslationResult {
  originalTerm: string;
  translatedTerm: string;
  tenantId: string;
}

export interface CreateTenantBrandingInput {
  tenantId: string;
  logoUrl?: string;
  faviconUrl?: string;
  primaryColor: string;
  secondaryColor: string;
  accentColor: string;
  fontFamily: string;
  companyName: string;
  companyTagline?: string;
  supportEmail?: string;
  supportPhone?: string;
  websiteUrl?: string;
  terminology: Record<string, string>;
  customDomain?: string;
  subdomain: string;
  websiteSlug?: string;
  address?: string;
  hours?: string;
  licenseType?: string;
  services?: string[];
  accreditations?: string[];
  hideFerpBranding: boolean;
  showHelpCenter: boolean;
  showSupportChat: boolean;
}

export interface UpdateTenantBrandingInput {
  tenantId: string;
  logoUrl?: string | null;
  faviconUrl?: string | null;
  primaryColor?: string;
  secondaryColor?: string;
  accentColor?: string;
  fontFamily?: string;
  companyName?: string;
  companyTagline?: string | null;
  supportEmail?: string | null;
  supportPhone?: string | null;
  websiteUrl?: string | null;
  terminology?: Record<string, string>;
  customDomain?: string | null;
  subdomain?: string;
  websiteSlug?: string | null;
  address?: string | null;
  hours?: string | null;
  licenseType?: string | null;
  services?: string[];
  accreditations?: string[];
  hideFerpBranding?: boolean;
  showHelpCenter?: boolean;
  showSupportChat?: boolean;
}

export interface ResetTenantBrandingInput {
  tenantId: string;
  subdomain: string;
  companyName: string;
  industry?: string; // healthcare, dental, legal, finance
}

export interface DeleteTenantBrandingInput {
  tenantId: string;
}

export interface TranslateTermInput {
  tenantId: string;
  term: string;
}
